//src/pages/HomePagetest.jsx
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from '../contexts/AuthContext'; // Uses updated AuthContext
import { useLoading } from '../contexts/LoadingContext';
import { getFormsForUser } from '../utils/firestore'; // Uses centralized Firestore fetch function
import HomeCard from '../components/Home/HomeCard';
import TemplateSection from '../components/Home/TemplateSection';
import HomeFormsTable from '../components/Home/HomeFormsTable';

import { db } from '../firebase'; // needed for per-form snapshot updates
import { doc, onSnapshot } from 'firebase/firestore'; // subscribe to screenshot field writes

import {
  Typography,
  Box,
  Pagination,
  Skeleton,
  TextField,
  MenuItem,
  Tooltip,
  IconButton,
} from '@mui/material';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import CreateFormWrapper from '../contexts/CreateFormWrapper';
import { resolveScreenshotUrlsForForms } from '../utils/formThumbnailResolver';

const PAGE_SIZE = 10; // Number of forms per page

// Match the cleaner Template Gallery FormCard visual dimensions.
// Keeps skeletons, loaded cards, and grid spacing aligned to avoid layout shift.
const HOME_CARD_WIDTH = 200;
const HOME_CARD_IMAGE_HEIGHT = 150;
const HOME_CARD_GRID_GAP_PX = 20;
const HOME_CARD_GRID_GAP = `${HOME_CARD_GRID_GAP_PX}px`;

// Enterprise workspace sizing.
//
// WHY
// - Google-style file surfaces keep the content area centered.
// - Grid and list/table views should maintain the same wrapper width so
//   switching views does not visually jump across the screen.
// - These constants are intentionally centralized so the layout can be
//   tuned later without hunting through JSX.
// - The rail width is derived from the card system instead of being
//   hand-picked. This keeps the toolbar aligned to the same visual width
//   as the forms grid and future list/table view.
//
// IMPORTANT
// - Page padding must NOT be counted inside this rail width.
// - The outer section owns responsive page padding.
// - The inner rail owns the exact grid/list content width.
const HOME_GRID_MAX_COLUMNS = 5;
const HOME_CONTENT_RAIL_WIDTH =
  (HOME_CARD_WIDTH * HOME_GRID_MAX_COLUMNS) +
  (HOME_CARD_GRID_GAP_PX * (HOME_GRID_MAX_COLUMNS - 1));
const HOME_CONTENT_X_PADDING = { xs: 2, sm: 3, md: 4 };
const HOME_CONTENT_Y_PADDING = 2;
const HOME_VIEW_MODE_STORAGE_KEY = 'homeFormsViewMode';

// View modes:
//
// grid = visual cards
// list = table/list view
const HOME_VIEW_MODES = Object.freeze({
  GRID: 'grid',
  LIST: 'list',
});

// Owner filters are config-driven so new filters can be added later
// without rewriting the toolbar component.
const HOME_OWNER_FILTERS = Object.freeze([
  { value: 'anyone', label: 'Owned by anyone' },
  { value: 'me', label: 'Owned by me' },
  { value: 'shared', label: 'Shared with me' },
]);

const homeCardGridStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  gap: HOME_CARD_GRID_GAP,
};

const renderHomeCardSkeletons = (
  keyPrefix = 'home-card-skeleton',
  count = 5,
) => (
  <div style={homeCardGridStyle}>
    {Array.from({ length: count }).map((_, index) => (
      <Box
        key={`${keyPrefix}-${index}`}
        sx={{
          minWidth: HOME_CARD_WIDTH,
          width: HOME_CARD_WIDTH,
        }}
      >
        <Skeleton
          variant="rectangular"
          width={HOME_CARD_WIDTH}
          height={HOME_CARD_IMAGE_HEIGHT}
          sx={{
            borderRadius: 0,
          }}
        />
        <Skeleton
          variant="text"
          width={170}
          sx={{
            mt: 1,
            ml: 0.5,
          }}
        />
        <Skeleton
          variant="text"
          width={120}
          sx={{
            ml: 0.5,
          }}
        />
      </Box>
    ))}
  </div>
);

const HomePagetest = () => {
  const { currentUser, organizationId, initialized, authLoading } = useAuth();
  const { setLoading } = useLoading();

  // 🔹 LOCAL STATES
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [loadingForms, setLoadingForms] = useState(true); // Track form loading state
  const [formsByPage, setFormsByPage] = useState({}); // 🔥 Store forms per page
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastVisibleDocs, setLastVisibleDocs] = useState({});

  // Google-style file surface state:
  // - Store the user's preferred view locally.
  // - Switching view modes must not refetch forms or recreate screenshot
  //   watchers. It only changes presentation.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const stored = localStorage.getItem(HOME_VIEW_MODE_STORAGE_KEY);
      return stored === HOME_VIEW_MODES.LIST
        ? HOME_VIEW_MODES.LIST
        : HOME_VIEW_MODES.GRID;
    } catch (_) {
      return HOME_VIEW_MODES.GRID;
    }
  });

  const [ownerFilter, setOwnerFilter] = useState('anyone');

  const prevUserId = useRef(null);

  // Track screenshot watchers for forms on the current page
  // Keyed by formId so we can unsubscribe cleanly as pages change.
  const screenshotWatchersRef = useRef(new Map());

  // Enterprise: stable callback state mirrors.
  //
  // WHY
  // - HomeCard is memoized and compares onDelete by reference.
  // - If handleDeleteForm depends directly on formsByPage/currentPage/etc.,
  //   every page-state change recreates onDelete and can force visible
  //   cards to re-render.
  // - These refs let handleDeleteForm stay stable while still reading the
  //   latest state at the moment the user archives a form.
  const formsByPageRef = useRef(formsByPage);
  const totalPagesRef = useRef(totalPages);
  const currentPageRef = useRef(currentPage);
  const lastVisibleDocsRef = useRef(lastVisibleDocs);
  const userIdRef = useRef(userId);
  const orgIdRef = useRef(orgId);

  useEffect(() => {
    formsByPageRef.current = formsByPage;
  }, [formsByPage]);

  useEffect(() => {
    totalPagesRef.current = totalPages;
  }, [totalPages]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    lastVisibleDocsRef.current = lastVisibleDocs;
  }, [lastVisibleDocs]);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    orgIdRef.current = orgId;
  }, [orgId]);

  // Current page forms are the single data source for both grid and list.
  // Switching view modes must not trigger a Firestore read.
  const currentPageForms = useMemo(() => (
    formsByPage[currentPage] || []
  ), [formsByPage, currentPage]);

  // Client-side owner filter for the currently loaded page.
  //
  // IMPORTANT
  // - This keeps the first implementation safe and UI-focused.
  // - If you want "Owned by me" to filter across *all pages* before
  //   pagination, the enterprise next step is to add server-side filter
  //   support to getFormsForUser.
  const visibleForms = useMemo(() => {
    if (ownerFilter === 'me') {
      return currentPageForms.filter((form) => (
        form.owner === userId || form.createdBy === userId
      ));
    }

    if (ownerFilter === 'shared') {
      return currentPageForms.filter((form) => {
        const sharedWithUser = Boolean(form.sharedWith?.[userId]);

        const orgWideAccess =
          form.organizationId === orgId &&
          form.orgAccess &&
          form.orgAccess !== 'none' &&
          form.owner !== userId &&
          form.createdBy !== userId;

        return sharedWithUser || orgWideAccess;
      });
    }

    return currentPageForms;
  }, [currentPageForms, ownerFilter, userId, orgId]);

  const handleToggleViewMode = useCallback(() => {
    setViewMode((prev) => {
      const next = prev === HOME_VIEW_MODES.GRID
        ? HOME_VIEW_MODES.LIST
        : HOME_VIEW_MODES.GRID;

      try {
        localStorage.setItem(HOME_VIEW_MODE_STORAGE_KEY, next);
      } catch (_) {
        // non-fatal
      }

      return next;
    });
  }, []);

  const handleOwnerFilterChange = useCallback((event) => {
    setOwnerFilter(event.target.value);
  }, []);


  // 1️⃣ Wait for AuthContext to be fully ready, then store user/org locally
  useEffect(() => {
    if (!initialized || authLoading) return; // Still loading, do nothing

    if (currentUser?.uid && organizationId) {
      setUserId(currentUser.uid);
      setOrgId(organizationId);
    } else {
      setUserId(null);
      setOrgId(null);
    }
  }, [initialized, authLoading, currentUser?.uid, organizationId]);

// 2️⃣ Fetch the latest forms per page
const fetchForms = useCallback(async (uid, orgId, page = 1) => {
  if (!uid || !orgId) return;

  try {
    setLoading(true, 'Forms');
    setLoadingForms(true); // ✅ Start loading forms

//    console.log(`[HomePagetest] 🔄 Fetching forms for Page ${page} from Firestore...`);

    // ✅ Get last document for pagination logic
    const lastDoc = page === 1 ? null : lastVisibleDocs[page - 1] || null;

    // 🔥 Fetch paginated forms
    const { status, forms: fetchedForms, lastVisibleDoc, totalPages: estimatedTotalPages } = await getFormsForUser(
      uid,
      orgId,
      lastDoc,
      PAGE_SIZE
    );

if (status === 'success' && fetchedForms.length > 0) {
  const enrichedForms = await resolveScreenshotUrlsForForms(fetchedForms, orgId);

  setFormsByPage((prev) => ({
    ...prev,
    [page]: enrichedForms,
  }));

      // ✅ Fix: Ensure totalPages **only increases when needed** (prevents extra pages)
      setTotalPages((prevTotal) => Math.max(estimatedTotalPages, prevTotal));

      // ✅ Update lastVisibleDocs for pagination consistency
      setLastVisibleDocs((prevDocs) => ({
        ...prevDocs,
        [page]: lastVisibleDoc || prevDocs[page - 1] || null, // Preserve last doc if no new results
      }));
    } else {
      console.warn(`[HomePagetest] ❌ No forms found on Page ${page}.`);

      setFormsByPage((prev) => ({
        ...prev,
        [page]: [],
      }));

      // ✅ Fix: Dynamically **reduce total pages** if an empty page is loaded
      setTotalPages((prevTotal) => Math.min(prevTotal, page - 1));

      // ✅ Ensure lastVisibleDocs remains valid
      setLastVisibleDocs((prevDocs) => ({
        ...prevDocs,
        [page]: prevDocs[page - 1] || null, // Preserve last doc reference if available
      }));
    }
  } catch (error) {
    console.error('[fetchForms] ❌ Error fetching forms:', error);
  } finally {
    setLoading(false, 'Forms');
    setLoadingForms(false); // ✅ Forms finished loading
  }
}, [setLoading, lastVisibleDocs]);

  // 3️⃣ Automatically fetch forms when user/org changes or when the page changes
  useEffect(() => {
    if (!userId || !orgId) return;

    if (userId !== prevUserId.current || !formsByPage[currentPage]) {
//      console.log(`[HomePagetest] 🔄 Fetching forms for Page ${currentPage}...`);
      fetchForms(userId, orgId, currentPage);
      prevUserId.current = userId;
    }
  }, [userId, orgId, currentPage, fetchForms, formsByPage]);

  useEffect(() => {
    if (!userId || !orgId) return;

    const watchers = screenshotWatchersRef.current;
    const pageAtSubscribe = currentPage; // capture page for stable updates

    const pageForms = formsByPage[currentPage] || [];

    // 1) Cleanup watchers for forms that are no longer on this page
    const idsOnPage = new Set(
      (Array.isArray(pageForms) ? pageForms : [])
        .map((f) => f?.id)
        .filter(Boolean),
    );

    for (const [formId, unsub] of watchers.entries()) {
      if (!idsOnPage.has(formId)) {
        try { unsub(); } catch (_) {}
        watchers.delete(formId);
      }
    }

    // If this page has no forms yet, we still did cleanup above.
    if (!Array.isArray(pageForms) || pageForms.length === 0) return;


    // 2) Attach watchers only for published forms whose screenshot is not yet available
    pageForms.forEach((f) => {
      if (!f?.id) return;
      if (f.status !== 'published') return;

      const missingPaths = !f.screenshotThumbnail && !f.screenshotPath;
      const notReadyStatus =
        typeof f.screenshotStatus === 'string' &&
        f.screenshotStatus !== 'ready' &&
        f.screenshotStatus !== 'error';

      // Watch when:
      // - the screenshot paths aren’t present yet, OR
      // - status exists and isn’t ready/error yet
      const needsWatch = missingPaths || notReadyStatus;
      if (!needsWatch) return;

      if (watchers.has(f.id)) return;

      // Capture the paths that existed when this watcher was attached.
      // This matters on republish:
      // - The backend keeps showing the old screenshot while the new one
      //   is queued/processing.
      // - We should NOT unsubscribe just because an old path exists.
      // - We should keep watching until screenshotStatus is "ready" or a
      //   path arrives for a form that had no screenshot at subscribe time.
      const previousThumbAtSubscribe = f.screenshotThumbnail || null;
      const previousPathAtSubscribe = f.screenshotPath || null;
      const hadMissingPathsAtSubscribe = missingPaths;

      const unsub = onSnapshot(
        doc(db, 'forms', f.id),
        (snap) => {
          if (!snap.exists()) return;

          const d = snap.data() || {};
          const nextThumb = d.screenshotThumbnail || null;
          const nextPath  = d.screenshotPath || null;
          const nextStatus = d.screenshotStatus || null;

          // If still not available and not explicitly errored, keep waiting.
          if (!nextThumb && !nextPath && nextStatus !== 'error') {
            return;
          }

          // Patch just this form on this page in-place.
          //
          // Enterprise efficiency:
          // - Avoid returning a new formsByPage object when nothing changed.
          // - This prevents unnecessary HomeCard re-renders from initial
          //   onSnapshot emissions that repeat the same screenshot fields.
          setFormsByPage((prev) => {
            const cur = prev[pageAtSubscribe] || [];
            let didChange = false;

            const updated = cur.map((x) => {
              if (x.id !== f.id) return x;

              const directRef = nextThumb ?? nextPath ?? x.screenshotUrl ?? null;

              const nextForm = {
                ...x,
                screenshotThumbnail: nextThumb ?? x.screenshotThumbnail ?? null,
                screenshotPath     : nextPath  ?? x.screenshotPath      ?? null,
                screenshotStatus   : nextStatus ?? x.screenshotStatus ?? null,
                screenshotUpdatedAt: d.screenshotUpdatedAt ?? x.screenshotUpdatedAt ?? null,
                screenshotUrl: directRef,
              };

              const changed =
                nextForm.screenshotThumbnail !== x.screenshotThumbnail ||
                nextForm.screenshotPath !== x.screenshotPath ||
                nextForm.screenshotStatus !== x.screenshotStatus ||
                nextForm.screenshotUpdatedAt !== x.screenshotUpdatedAt ||
                nextForm.screenshotUrl !== x.screenshotUrl;

              if (!changed) return x;

              didChange = true;
              return nextForm;
            });

            if (!didChange) return prev;

            return { ...prev, [pageAtSubscribe]: updated };
          });


          // Stop watching only when the screenshot lifecycle is safely done.
          //
          // Enterprise:
          // - Do NOT stop just because a path exists; on republish, that
          //   path can be the previous screenshot while the new job is still
          //   queued/processing.
          // - Do NOT stop only because status is "error" — a republish can
          //   re-queue and later write paths.
          // - Stop when status is explicitly ready.
          // - Also stop for legacy/first-publish flows where a path arrives
          //   without a queued/processing state.
          const hasPath = Boolean(nextThumb || nextPath);
          const isReady = nextStatus === 'ready';
          const isInFlight =
            nextStatus === 'queued' ||
            nextStatus === 'processing';

          const pathChanged =
            (nextThumb && nextThumb !== previousThumbAtSubscribe) ||
            (nextPath && nextPath !== previousPathAtSubscribe);

          const pathArrivedForFirstPublish =
            hadMissingPathsAtSubscribe &&
            hasPath &&
            !isInFlight;

          const safeToStopWatching =
            isReady ||
            pathArrivedForFirstPublish ||
            (pathChanged && !isInFlight);

          if (safeToStopWatching) {
            const u = watchers.get(f.id);
            if (u) {
              try { u(); } catch (_) {}
              watchers.delete(f.id);
            }
          }
        },
        (err) => {

          console.warn('[HomePagetest] screenshot watcher error (non-fatal):', {
            formId: f.id,
            msg: err?.message || String(err),
          });
          const u = watchers.get(f.id);
          if (u) {
            try { u(); } catch (_) {}
            watchers.delete(f.id);
          }
        }
      );

      watchers.set(f.id, unsub);
    });

    // 3) On unmount, clean everything
    // IMPORTANT:
    // - This effect re-runs frequently (formsByPage changes).
    // - React will call the cleanup BEFORE every re-run.
    // - If we unsubscribe everything here, watchers will never stay alive.
    // So: keep this cleanup as a no-op and do a real unmount cleanup
    // in a separate useEffect([]) below.
    return () => {};

  }, [userId, orgId, currentPage, formsByPage]);

  // Unmount safety — unsubscribe all screenshot watchers once
  // This runs ONLY when the HomePagetest component unmounts.
  useEffect(() => {
    return () => {
      try {
        const w = screenshotWatchersRef.current;
        for (const [, unsub] of w.entries()) {
          try { unsub(); } catch (_) {}
        }
        w.clear();
      } catch (_) {
        // non-fatal
      }
    };
  }, []);


  // Pagination handler
  const handlePageChange = (event, value) => {
    if (value <= totalPages) {
      setCurrentPage(value);
    }
  };

// 🗑️  remove the form **locally** (optimistic UI) --------------------------
const handleDeleteForm = useCallback(
  async (deletedId) => {
    const currentPageSnapshot = currentPageRef.current;
    const totalPagesSnapshot = totalPagesRef.current;
    const userIdSnapshot = userIdRef.current;
    const orgIdSnapshot = orgIdRef.current;
    const lastVisibleDocsSnapshot = lastVisibleDocsRef.current;
    const formsByPageSnapshot = formsByPageRef.current;

    /* --------------------------------------------------------------------
       1️⃣  SNAPSHOT the current page BEFORE the optimistic update
           We need this to decide whether a back-fill is required.
    ---------------------------------------------------------------------*/
    const pageFormsBefore = formsByPageSnapshot[currentPageSnapshot] ?? [];
    const updatedPageForms = pageFormsBefore.filter(
      f => f.id !== deletedId
    );

    const willNeedFill =
      updatedPageForms.length < PAGE_SIZE &&
      currentPageSnapshot < totalPagesSnapshot;

    /* --------------------------------------------------------------------
       2️⃣  OPTIMISTIC LOCAL UPDATE
           – Remove the card immediately so the grid re-flows with no flicker
    ---------------------------------------------------------------------*/
    setFormsByPage(prev => ({
      ...prev,
      [currentPageSnapshot]: (prev[currentPageSnapshot] ?? [])
        .filter(f => f.id !== deletedId),
    }));

    console.log(
      `[HomePagetest] 🗑️  Deleting ${deletedId}. ` +
      `Page ${currentPageSnapshot} size ${pageFormsBefore.length} → ` +
      `${updatedPageForms.length}.  ` +
      (willNeedFill ? 'Back-fill required.' : 'No back-fill needed.')
    );

    /* --------------------------------------------------------------------
       3️⃣  OPTIONAL BACK-FILL
           If the current page now has < PAGE_SIZE items and there is
           another page, pull *one* extra record silently (no skeletons).

           Enterprise:
           – The back-filled form must go through the same screenshot
             resolver as the initial page fetch. Otherwise legacy forms that
             only resolve through assets lookup can appear without thumbnails
             until a full refresh.
    ---------------------------------------------------------------------*/
    if (!willNeedFill) return;

    if (!userIdSnapshot || !orgIdSnapshot) {
      console.warn(
        '[HomePagetest] ⚠️  Back-fill skipped because user/org context is missing.',
        {
          userId: userIdSnapshot,
          orgId: orgIdSnapshot,
        }
      );
      return;
    }

    try {
      console.log('[HomePagetest] 🔄 Fetching one record to back-fill…');

      const { forms: extra, lastVisibleDoc: extraLast } = await getFormsForUser(
        userIdSnapshot,
        orgIdSnapshot,
        lastVisibleDocsSnapshot[currentPageSnapshot] ?? null,  // start where this page ended
        1                                                      // only need one
      );

      const extraForms = Array.isArray(extra) ? extra : [];
      const enrichedExtra = extraForms.length > 0
        ? await resolveScreenshotUrlsForForms(extraForms, orgIdSnapshot)
        : [];

      if (enrichedExtra?.[0]) {
        setFormsByPage(prev => ({
          ...prev,
          [currentPageSnapshot]: [
            ...(prev[currentPageSnapshot] ?? []),
            enrichedExtra[0],
          ],
        }));

        // keep pagination pointer correct for any further back-fills
        setLastVisibleDocs(prev => ({
          ...prev,
          [currentPageSnapshot]: extraLast ?? prev[currentPageSnapshot],
        }));


        console.log(
          `[HomePagetest] ➕ Added ${enrichedExtra[0].id} to end of page ${currentPageSnapshot}`
        );
      } else {
        console.log('[HomePagetest] ⚠️  No extra records available for back-fill');
      }
    } catch (err) {
      console.error('[HomePagetest] ❌ Back-fill fetch failed:', err);
    }
  },
  []
);



  // 6️⃣ Navigation handlers
  // HomeCard owns its own configure/submissions/preview navigation.
  // Keep HomePagetest focused on data loading, pagination, screenshot
  // watcher state, and optimistic archive/back-fill behavior.

// 7️⃣ TemplateSection is ALWAYS visible, but forms show Skeleton Loaders while loading
return (
  <div>
    {/* ✅ TemplateSection is visible immediately (BUT only after auth is ready to prevent Storage 403 spam) */}
    {initialized && !authLoading && userId && orgId ? (
      <TemplateSection />
    ) : (
      renderHomeCardSkeletons('template-auth')
    )}


    {/* 🔹 Show Skeletons for forms while loading */}
    {!initialized || authLoading || !userId || !orgId || loadingForms ? (
      <Box
        component="section"
        sx={{
          px: HOME_CONTENT_X_PADDING,
          py: HOME_CONTENT_Y_PADDING,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            maxWidth: HOME_CONTENT_RAIL_WIDTH,
            mx: 'auto',
            width: '100%',
          }}
        >
          {renderHomeCardSkeletons('forms-loading')}
        </Box>
      </Box>
    ) : (
      <Box
        component="section"
        sx={{
          px: HOME_CONTENT_X_PADDING,
          py: HOME_CONTENT_Y_PADDING,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            maxWidth: HOME_CONTENT_RAIL_WIDTH,
            mx: 'auto',
            width: '100%',
          }}
        >
        {/* Google-style subheader / view controls */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            mb: 2,
            width: '100%',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
            }}
          >
            Recent forms
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'space-between', sm: 'flex-end' },
              gap: 1,
              width: { xs: '100%', sm: 'auto' },
              minWidth: 0,
            }}
          >
            <TextField
              select
              size="small"
              value={ownerFilter}
              onChange={handleOwnerFilterChange}
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      mt: 0.5,
                      borderRadius: 2,
                    },
                  },
                },
              }}
              sx={{
                minWidth: { xs: 170, sm: 180 },
                maxWidth: '100%',
                '& .MuiInputBase-root': {
                  borderRadius: 1,
                  px: 1,
                  py: 0.25,
                  color: 'text.secondary',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  py: 0.5,
                },
                '& .MuiSelect-icon': {
                  color: 'text.secondary',
                },
              }}
            >
              {HOME_OWNER_FILTERS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Google-style: show the icon for the NEXT view, not both icons */}
            <Tooltip
              title={
                viewMode === HOME_VIEW_MODES.GRID
                  ? 'Switch to list view'
                  : 'Switch to grid view'
              }
            >
              <IconButton
                size="small"
                onClick={handleToggleViewMode}
                aria-label={
                  viewMode === HOME_VIEW_MODES.GRID
                    ? 'Switch to list view'
                    : 'Switch to grid view'
                }
                sx={{
                  color: 'text.secondary',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {viewMode === HOME_VIEW_MODES.GRID ? (
                  <ViewListOutlinedIcon fontSize="small" />
                ) : (
                  <ViewModuleOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {visibleForms.length > 0 ? (
          viewMode === HOME_VIEW_MODES.GRID ? (
            <Box
              sx={{
                ...homeCardGridStyle,
                width: 'fit-content',
                maxWidth: '100%',
              }}
            >
              {visibleForms.map((form) => (
                <HomeCard
                  key={form.id}
                  form={form}
                  onDelete={handleDeleteForm}
                />
              ))}
            </Box>
          ) : (
            <HomeFormsTable
              forms={visibleForms}
              onDelete={handleDeleteForm}
            />
          )
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '50vh',
              width: '100%',
              textAlign: 'center',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              padding: '20px'
            }}
          >
            <DescriptionIcon sx={{ fontSize: 40, color: '#9e9e9e', marginBottom: '14px' }} />
            <Typography variant="h5" gutterBottom sx={{ color: '#9e9e9e' }}>
              No Forms Available
            </Typography>
            <CreateFormWrapper />
          </Box>
        )}
        </Box>
      </Box>
    )}

    {/* Pagination UI (only appears when forms are loaded) */}
    {totalPages > 1 && !loadingForms && (
      <Box
        sx={{
          px: HOME_CONTENT_X_PADDING,
          width: '100%',
          boxSizing: 'border-box',
          margin: '20px 0',
        }}
      >
        <Box
          sx={{
            maxWidth: HOME_CONTENT_RAIL_WIDTH,
            mx: 'auto',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          variant="outlined"
          shape="rounded"
          size="small"
          sx={{
            '& .MuiPaginationItem-root': {
              fontSize: '0.75rem',
              fontWeight: 400,
              backgroundColor: 'transparent',
              color: 'lightblue',
              border: 'none',
              cursor: 'pointer',
              transition: 'color 0.3s',
              '&:hover': {
                color: '#1976d2',
              },
            },
            '& .Mui-selected': {
              color: '#1976d2',
              fontWeight: 'bold',
            },
            '& .MuiPagination-ul': {
              gap: '8px',
            },
          }}
        />
        </Box>
      </Box>
    )}
  </div>
);

};

export default React.memo(HomePagetest);
